/**
 * NotificationPrefs — client-side (localStorage-only, no backend endpoint)
 * preference panel letting the owner choose which notification *kinds* are
 * allowed to surface in the bell (list + unread badge). Default: everything
 * ON — a kind is hidden only once the owner explicitly turns it off, so a
 * fresh browser/profile sees the full, unfiltered feed (never a silently
 * empty bell).
 *
 * Storage is a single JSON object of *overrides* (`{ [kind]: boolean }`) at
 * `psn.notifications.kindPrefs.v1` — a kind absent from the object is ON by
 * default, so shipping a brand-new kind (e.g. a 5th AI-staff kind added
 * later) surfaces it immediately without a migration. Every localStorage
 * access is wrapped in try/catch (private browsing / disabled storage must
 * never crash the bell) and reads degrade to "everything on".
 *
 * `useNotificationKindPrefs` is also consumed by `./useNotifications.ts` to
 * filter the feed itself — this file has no dependency on that hook, so the
 * import only goes one way (no cycle). A same-tab pub-sub plus a `storage`
 * listener keep every mounted consumer (the bell's list AND this panel, and
 * across browser tabs) in sync the instant a checkbox is toggled.
 *
 * IMPORTANT: server-side notification preferences (persisted per-account,
 * enforced at the API so a muted kind isn't even fetched) would need a new
 * backend endpoint — out of scope for this client-only panel; see the
 * FE-LANE 5 report.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  KNOWN_NOTIFICATION_KINDS,
  NOTIFICATION_KIND_INFO,
  type NotificationGroup,
} from '@/lib/api/notifications';
import { Modal } from '@/ui';

const STORAGE_KEY = 'psn.notifications.kindPrefs.v1';

/** kind -> surfaced in the bell. A key absent from the map means "on"
 *  (the default) — only explicit `false` overrides hide a kind. */
export type NotificationKindPrefs = Record<string, boolean>;

function readPrefs(): NotificationKindPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: NotificationKindPrefs = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'boolean') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writePrefs(prefs: NotificationKindPrefs): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable (private mode / quota) — the choice simply doesn't
    // persist across reloads; it still applies for the rest of this session
    // via the in-memory pub-sub below.
  }
}

// Same-tab reactivity: localStorage's native `storage` event only fires in
// OTHER tabs/windows, never the one that called setItem. This module-level
// listener set lets every mounted `useNotificationKindPrefs()` instance in
// THIS tab (the bell's list + the prefs panel, possibly open at once) pick up
// a change immediately.
const listeners = new Set<() => void>();
function emitLocalChange(): void {
  listeners.forEach((fn) => fn());
}

/** True unless the kind was explicitly turned off. */
export function isKindSurfaced(prefs: NotificationKindPrefs, kind: string | null | undefined): boolean {
  if (typeof kind !== 'string' || !kind) return true;
  return prefs[kind] !== false;
}

export interface UseNotificationKindPrefs {
  prefs: NotificationKindPrefs;
  isSurfaced: (kind: string | null | undefined) => boolean;
  setKindEnabled: (kind: string, on: boolean) => void;
  /** Clears every override — every kind goes back to the ON default. */
  resetAll: () => void;
}

export function useNotificationKindPrefs(): UseNotificationKindPrefs {
  const [prefs, setPrefs] = useState<NotificationKindPrefs>(() => readPrefs());

  useEffect(() => {
    const onLocalChange = () => setPrefs(readPrefs());
    listeners.add(onLocalChange);
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY || event.key === null) setPrefs(readPrefs());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(onLocalChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setKindEnabled = useCallback((kind: string, on: boolean) => {
    const next = { ...readPrefs(), [kind]: on };
    writePrefs(next);
    setPrefs(next);
    emitLocalChange();
  }, []);

  const resetAll = useCallback(() => {
    writePrefs({});
    setPrefs({});
    emitLocalChange();
  }, []);

  const isSurfaced = useCallback((kind: string | null | undefined) => isKindSurfaced(prefs, kind), [prefs]);

  return { prefs, isSurfaced, setKindEnabled, resetAll };
}

/* --------------------------------- panel --------------------------------- */

const GROUP_LABEL: Record<NotificationGroup, string> = {
  ai_staff: 'Équipe IA',
  collaboration: 'Collaboration',
};

function KindRow({
  kind,
  icon,
  label,
  enabled,
  onToggle,
}: {
  kind: string;
  icon: string;
  label: string;
  enabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
        <span aria-hidden="true" className="shrink-0 text-base leading-none">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`${label} — ${enabled ? 'affiché' : 'masqué'} dans la cloche`}
        onClick={() => onToggle(!enabled)}
        data-kind={kind}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          enabled ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
            enabled ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </li>
  );
}

export interface NotificationPrefsProps {
  onClose: () => void;
}

/**
 * The preferences panel itself — a Modal (per `./NotificationBell.tsx`'s
 * gear button) listing every known kind, grouped Équipe IA / Collaboration,
 * each with an on/off switch. Changes apply immediately (no "Enregistrer"
 * step — every toggle writes straight to localStorage) and a "Réinitialiser"
 * footer action clears all overrides back to the all-on default.
 */
export function NotificationPrefs({ onClose }: NotificationPrefsProps) {
  const { prefs, isSurfaced, setKindEnabled, resetAll } = useNotificationKindPrefs();

  const groups: NotificationGroup[] = ['ai_staff', 'collaboration'];

  return (
    <Modal
      title="Préférences de notifications"
      onClose={onClose}
      size="sm"
      footer={
        <button
          type="button"
          onClick={resetAll}
          disabled={Object.keys(prefs).length === 0}
          className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
        >
          Réinitialiser (tout afficher)
        </button>
      }
    >
      <div className="space-y-5">
        <p className="text-xs text-muted">
          Choisissez les types de notifications à afficher dans la cloche. Ce réglage est
          enregistré uniquement sur cet appareil (aucune synchronisation entre appareils).
        </p>
        {groups.map((group) => {
          const kinds = KNOWN_NOTIFICATION_KINDS.filter(
            (kind) => NOTIFICATION_KIND_INFO[kind].group === group,
          );
          if (kinds.length === 0) return null;
          return (
            <div key={group}>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {GROUP_LABEL[group]}
              </h3>
              <ul className="divide-y divide-border">
                {kinds.map((kind) => (
                  <KindRow
                    key={kind}
                    kind={kind}
                    icon={NOTIFICATION_KIND_INFO[kind].icon}
                    label={NOTIFICATION_KIND_INFO[kind].label}
                    enabled={isSurfaced(kind)}
                    onToggle={(on) => setKindEnabled(kind, on)}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
